import React, { useState, useEffect, useCallback, useRef } from "react";
import DataTable from "react-data-table-component";
import "../../assets/css/style.css";
import { customStyles } from "../styles/table-style";
import BASE_URL from "../../assets/global/url";
import axios from "axios";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ThreeDot } from "react-loading-indicators";
import NoAccess from "../../assets/img/NoAccess.png";
import swal from "sweetalert";
import useDecodeToken from "../../hooks/customHook/useDecodeToken";
import { format } from "date-fns";
import { PaginationControls } from "../../hooks/customHook/paginationHook/usePagination";
import { useServerPagination } from "../../hooks/customHook/paginationHook/useServerPagination";
import DatePicker from "react-datepicker";
import {
  compactNumberFormat,
  currencySymbol,
  formatCurrency,
  formatToPHP,
  formatToTwoDecimal,
} from "../../utils/numberFormatter";
import DateRangePicker from "../../components/DateRangePicker";
import { getMonthBoundaries, initializeCutoff } from "../../utils/newdate";
import { Button, Modal, Tab, Tabs } from "react-bootstrap";
import FilterStore from "../../hooks/customHook/FilterStore";
import { useServerSort } from "../../hooks/customHook/sortingHook/useTableSort";

const NewFixedAsset = ({ authrztn }) => {
  const navigate = useNavigate();
  const userLoggedID = useDecodeToken();
  const [isLoading, setIsLoading] = useState(true); // For fixed asset module loading state
  const [cutOffDate, setCutOffDate] = useState(""); // For date range

  // For fixed asset modal
  const [showFixedAsset, setShowFixedAsset] = useState(false);

  // For search filter
  const [searchText, setSearchText] = useState(() => FilterStore.FAgetSearch());
  useEffect(() => {
    FilterStore.FAsaveSearch(searchText);
  }, [searchText]);
  const [filterColumn, setFilterColumn] = useState("all");
  const debounceTimer = useRef(null);
  const pageRestored = useRef(false);
  const isFirstFilterReset = useRef(true);

  // State for fixed asset widgets
  const [widgets, setWidgets] = useState({
    totalCost: 0,
    depreciatedAmount: 0,
    netValue: 0,
  });

  //Tab Change
  const [activeTab, setActiveTab] = useState(() => {
    const savedTab = FilterStore.getTab();
    return ["depreciation", "expenses", "masterlist"].includes(savedTab)
      ? savedTab
      : "masterlist";
  });

  // For forecast table pagination
  const forecastPagination = useServerPagination(
    `${BASE_URL}/fixedasset/v2/forecast`,
    FilterStore.FAgetPerPage(),
  );

  // Fetch pending fixed assets (For Modal)
  const pendingFixedAssetsPagination = useServerPagination(
    `${BASE_URL}/fixedasset/v2/getFixedAsset`,
    10,
    { status: "Pending" },
  );

  // Fetch approved fixed assets (For Expenses Tab)
  const approvedFixedAssetsPagination = useServerPagination(
    `${BASE_URL}/fixedasset/v2/getFixedAsset`,
    FilterStore.FAagetPerPage(),
    { status: "Approved" },
  );

  // Fetch all fixed assets regardless of status (For Masterlist Tab)
  const masterlistPagination = useServerPagination(
    `${BASE_URL}/fixedasset/v2/getFixedAsset`,
    FilterStore.FAmgetPerPage(),
  );

  const forecastSort = useServerSort(forecastPagination);
  const pendingSort = useServerSort(pendingFixedAssetsPagination);
  const approvedSort = useServerSort(approvedFixedAssetsPagination);
  const masterlistSort = useServerSort(masterlistPagination);

  // Initialize date range and fetch table, applying FilterStore values if they exist
  const fetchCutOff = () => {
    const savedFilter = FilterStore.get();
    const savedSearch = FilterStore.FAgetSearch();
    let initialCutoff;

    // const boundaries = getMonthBoundaries();
    // const initialCutoff = initializeCutoff(boundaries);

    // Hydrate date range
    if (savedFilter) {
      initialCutoff = savedFilter;
    } else {
      const boundaries = getMonthBoundaries();
      initialCutoff = initializeCutoff(boundaries);
      FilterStore.save(initialCutoff.from, initialCutoff.to); // Persist default
    }

    setCutOffDate({
      from: initialCutoff.from,
      to: initialCutoff.to,
    });

    // Hydrate search string
    if (savedSearch) {
      setSearchText(savedSearch);
      forecastPagination.updateApiUrl(
        `${BASE_URL}/fixedasset/v2/forecast/search`,
      );
    }

    reloadTable(initialCutoff, savedSearch, filterColumn);
  };

  // For fixed asset widgets data
  const fetchWidgets = async (cutoff) => {
    try {
      const response = await axios.get(`${BASE_URL}/fixedasset/widgets`, {
        params: {
          startDate: cutoff?.from,
          endDate: cutoff?.to,
        },
      });

      setWidgets(response.data);
    } catch (error) {
      console.error(error);
    }
  };

  // Handle data table and widgets fetching
  const reloadTable = (
    cutoff,
    currentSearchText = searchText,
    currentFilterCol = filterColumn,
  ) => {
    // forecastPagination.updateParams({
    //   startDate: cutoff?.from,
    //   endDate: cutoff?.to,
    // });

    const forecastParams = {
      startDate: cutoff?.from,
      endDate: cutoff?.to,
    };

    const approvedParams = {
      startDate: cutoff?.from,
      endDate: cutoff?.to,
      status: "Approved",
    };

    const masterlistParams = {
      startDate: cutoff?.from,
      endDate: cutoff?.to,
    };

    if (currentSearchText) {
      forecastParams.searchText = currentSearchText;
      forecastParams.filterColumn = currentFilterCol;
      approvedParams.searchText = currentSearchText;
      approvedParams.filterColumn = currentFilterCol;
      masterlistParams.searchText = currentSearchText;
      masterlistParams.filterColumn = currentFilterCol;
    }

    forecastPagination.updateParams(forecastParams);
    approvedFixedAssetsPagination.updateParams(approvedParams);
    masterlistPagination.updateParams(masterlistParams);

    // Pending Modal Pagination
    pendingFixedAssetsPagination.updateParams({
      startDate: cutoff?.from,
      endDate: cutoff?.to,
      status: "Pending",
    });

    fetchWidgets(cutoff);
    setIsLoading(false);
  };

  // Update search text and refresh table results
  const handleSearchChange = useCallback(
    (value) => {
      setSearchText(value);
      FilterStore.FAsaveSearch(value);

      if (debounceTimer.current) clearTimeout(debounceTimer.current);

      debounceTimer.current = setTimeout(() => {
        // forecastPagination.updateApiUrl(
        //   `${BASE_URL}/fixedasset/v2/forecast/search`
        // );

        // Toggle to search endpoint if there is a value
        if (value) {
          forecastPagination.updateApiUrl(
            `${BASE_URL}/fixedasset/v2/forecast/search`,
          );
        } else {
          forecastPagination.updateApiUrl(`${BASE_URL}/fixedasset/v2/forecast`);
        }

        forecastPagination.updateParams({
          startDate: cutOffDate?.from,
          endDate: cutOffDate?.to,
          searchText: value,
          filterColumn: filterColumn,
        });
      }, 500);

      approvedFixedAssetsPagination.updateParams({
        startDate: cutOffDate?.from,
        endDate: cutOffDate?.to,
        status: "Approved",
        searchText: value,
        filterColumn: filterColumn,
      });

      masterlistPagination.updateParams({
        startDate: cutOffDate?.from,
        endDate: cutOffDate?.to,
        searchText: value,
        filterColumn: filterColumn,
      });
    },
    [
      cutOffDate,
      filterColumn,
      forecastPagination,
      approvedFixedAssetsPagination,
      masterlistPagination,
    ],
  );

  // Open fixed asset modal
  const handleOpenFixedAsset = () => {
    setShowFixedAsset(true);
  };

  // Closes fixed asset modal
  const handleCloseFixedAsset = () => {
    setShowFixedAsset(false);
  };

  const handleFilterColumn = useCallback(
    (column) => {
      setFilterColumn(column);
      if (searchText) {
        forecastPagination.updateParams({
          startDate: cutOffDate?.from,
          endDate: cutOffDate?.to,
          searchText: searchText,
          filterColumn: column,
        });

        approvedFixedAssetsPagination.updateParams({
          startDate: cutOffDate?.from,
          endDate: cutOffDate?.to,
          status: "Approved",
          searchText: searchText,
          filterColumn: column,
        });

        masterlistPagination.updateParams({
          startDate: cutOffDate?.from,
          endDate: cutOffDate?.to,
          searchText: searchText,
          filterColumn: column,
        });
      }
    },
    [
      cutOffDate,
      searchText,
      forecastPagination,
      approvedFixedAssetsPagination,
      masterlistPagination,
    ],
  );

  // Handles the payment process for a fixed asset forecast
  const handlePayForecast = async (fixedAssetForecastId) => {
    try {
      const payForecast = await swal({
        icon: "warning",
        title: "Confirm Forecast Payment",
        text: "Are you sure you want to proceed with this fixed asset forecast payment?",
        buttons: true,
        dangerMode: true,
      });

      if (payForecast) {
        const res = await axios.patch(
          `${BASE_URL}/fixedasset/forecast/${fixedAssetForecastId}/payments`,
        );

        if (res.status === 200) {
          await swal({
            icon: "success",
            title: "Payment Successful",
            text: "The fixed asset forecast has been marked as paid.",
          });

          reloadTable(cutOffDate);
        }
      }
    } catch (error) {
      if (error.response.status === 401) {
        swal({
          icon: "error",
          // title: "Please contact your support",
          title: error.response.data.title,
          text: error.response.data.message,
        }); // test 1
      } else {
        console.error(error);
      }
    }
  };

  // Marks a fixed asset forecast as passed
  const handlePassForecast = async (fixedAssetId, fixedAssetForecastId) => {
    try {
      const passForecast = await swal({
        icon: "warning",
        title: "Mark Forecast as Passed?",
        text: "This will mark the selected fixed asset forecast as passed and generate a new forecast month. Do you want to continue?",
        buttons: true,
        dangerMode: true,
      });

      if (passForecast) {
        const res = await axios.patch(
          `${BASE_URL}/fixedasset/forecast/${fixedAssetForecastId}`,
          {
            fixedAssetId,
          },
        );

        if (res.status === 200) {
          await swal({
            icon: "success",
            title: "Forecast Passed",
            text: "The fixed asset forecast has been marked as passed.",
          });

          reloadTable(cutOffDate);
        }
      }
    } catch (error) {
      if (error.response.status === 401) {
        swal({
          icon: "error",
          // title: "Please contact your support",
          title: error.response.data.title,
          text: error.response.data.message,
        }); // test 1
      } else {
        console.error(error);
      }
    }
  };

  // Handle table row click
  const handleRowClicked = (fixedAssetId) => {
    navigate(`/accounting/view-fixed-asset/${fixedAssetId}`);
  };

  // Handle date range change
  const handleDateRangeChange = (startDate, endDate) => {
    const cutoff = {
      id: "custom",
      from: startDate,
      to: endDate,
    };

    setCutOffDate(cutoff);
    FilterStore.save(startDate, endDate);

    setSearchText("");
    FilterStore.FAsaveSearch("");

    // Reset all paginations to page 1 on date change
    forecastPagination.setCurrentPage(1);
    FilterStore.FAsavePage(1);
    approvedFixedAssetsPagination.setCurrentPage(1);
    FilterStore.FAasavePage(1);
    masterlistPagination.setCurrentPage(1);
    FilterStore.FAmsavePage(1);

    forecastPagination.updateApiUrl(`${BASE_URL}/fixedasset/v2/forecast`);
    reloadTable(cutoff, "");
  };

  // Handle clearing all filters (date range and search)
  const handleClearFilters = () => {
    const boundaries = getMonthBoundaries();
    const initialCutoff = initializeCutoff(boundaries);

    FilterStore.clear();
    FilterStore.FAsaveSearch("");

    setCutOffDate(initialCutoff);
    setSearchText("");
    setFilterColumn("all");
    forecastPagination.updateApiUrl(`${BASE_URL}/fixedasset/v2/forecast`);
    forecastPagination.setCurrentPage(1);
    FilterStore.FAsavePage(1);
    approvedFixedAssetsPagination.setCurrentPage(1);
    FilterStore.FAasavePage(1);
    masterlistPagination.setCurrentPage(1);
    FilterStore.FAmsavePage(1);

    reloadTable(initialCutoff, "");
  };

  // For forecast data table columns
  const forecastColumns = [
    {
      name: "Date",
      selector: (row) => `${row.date} (${row.transactionNumber})`,
      sortField: "date",
    },
    {
      name: "Amount",
      selector: (row) => formatToTwoDecimal(row.amount),
      sortField: "amount",
    },
    {
      name: "Action",
      selector: (row) => (
        <div className="d-flex align-items-center gap-3">
          {/* Depreciate button */}
          <div style={{ width: "120px" }} className="text-center">
            {row.isPaid ? (
              <span className="badge w-100 py-2 fs-6 bg-success-subtle text-success border border-success-subtle">
                Depreciated
              </span>
            ) : (
              <button
                type="button"
                onClick={() => {
                  handlePayForecast(row.id);
                }}
                className="btn btn-warning w-100"
                disabled={row.isPassed}
              >
                Depreciate
              </button>
            )}
          </div>

          {/* Pass button */}
          <div style={{ width: "120px" }} className="text-center">
            {row.isPassed ? (
              <span className="badge w-100 py-2 fs-6 bg-success-subtle text-success border border-success-subtle">
                Passed
              </span>
            ) : (
              <button
                type="button"
                onClick={() => handlePassForecast(row.fixed_asset_id, row.id)}
                className="btn btn-warning w-100"
                disabled={row.isPaid}
              >
                Pass
              </button>
            )}
          </div>
        </div>
      ),
    },
  ];

  // For fixed asset data table columns
  const fixedAssetColumns = [
    {
      name: "Transaction ID",
      selector: (row) => row.transaction_code,
      sortField: "transaction_code",
    },
    {
      name: "Date Depreciated",
      selector: (row) => format(row.date_depreciated, "MMM/dd/yyyy"),
      sortField: "date_depreciated",
    },
    {
      name: "Item Name",
      selector: (row) => row.product_name,
      sortField: "product_name",
    },
    {
      name: "Total Cost",
      selector: (row) => formatToTwoDecimal(row.total_cost),
      sortField: "total_cost",
    },
    {
      name: "Remarks",
      selector: (row) => row.remarks || "--",
      sortField: "remarks",
    },
  ];

  const expensesColumns = [
    {
      name: "Transaction ID",
      selector: (row) => row.transaction_code ?? "---",
      sortField: "transaction_code",
    },
    {
      name: "Date Depreciated",
      selector: (row) => format(row.date_depreciated, "MMM/dd/yyyy") ?? "---",
      sortField: "date_depreciated",
    },
    {
      name: "Item Name",
      selector: (row) => row.product_name ?? "---",
      sortField: "product_name",
    },
    {
      name: "Remaining Expenses",
      selector: (row) =>
        row.remaining_expenses !== undefined
          ? formatCurrency(row.remaining_expenses, row.currency.currency_name)
          : "---",
      sortField: "remaining_expenses",
    },
    {
      name: "Total Cost",
      selector: (row) =>
        row.total_cost !== undefined
          ? formatCurrency(row.total_cost, row.currency.currency_name)
          : "---",
      sortField: "total_cost",
    },
  ];

  // Badge color per fixed asset status
  const statusBadgeClass = {
    Approved: "bg-success-subtle text-success border-success-subtle",
    Pending: "bg-warning-subtle text-warning border-warning-subtle",
    Rejected: "bg-danger-subtle text-danger border-danger-subtle",
  };

  // For masterlist data table columns
  const masterlistColumns = [
    {
      name: "Transaction ID",
      selector: (row) => row.transaction_code ?? "---",
      sortField: "transaction_code",
    },
    {
      name: "Date Depreciated",
      selector: (row) =>
        row.date_depreciated
          ? format(row.date_depreciated, "MMM/dd/yyyy")
          : "---",
      sortField: "date_depreciated",
    },
    {
      name: "Item Name",
      selector: (row) => row.product_name ?? "---",
      sortField: "product_name",
    },
    {
      name: "Quantity",
      selector: (row) => row.quantity ?? "---",
      sortField: "quantity",
    },
    {
      name: "Cost Per Unit",
      selector: (row) =>
        row.cost_per_unit != null
          ? formatCurrency(row.cost_per_unit, row.currency?.currency_name)
          : "---",
      sortField: "cost_per_unit",
    },
    {
      name: "Total Cost",
      selector: (row) =>
        row.total_cost != null
          ? formatCurrency(row.total_cost, row.currency?.currency_name)
          : "---",
      sortField: "total_cost",
    },
    {
      name: "Months",
      selector: (row) => row.static_months_to_pay ?? "---",
      sortField: "static_months_to_pay",
    },
    {
      name: "Monthly Depreciation",
      selector: (row) =>
        row.depreciation_amount != null
          ? formatCurrency(row.depreciation_amount, row.currency?.currency_name)
          : "---",
      sortField: "depreciation_amount",
    },
    {
      name: "Status",
      selector: (row) => (
        <span
          className={`badge border py-2 px-3 ${
            statusBadgeClass[row.status] ||
            "bg-secondary-subtle text-secondary border-secondary-subtle"
          }`}
        >
          {row.status ?? "---"}
        </span>
      ),
      sortField: "status",
    },
  ];

  ///PARA ITO SA FILTER RETENTION NG PAGE (same pattern as AC / Branches / UserManagement / Currency)
  //START
  // Save forecastPagination currentPage. Gate is closed until after mount
  // restore so the hook's internal reset-to-1 doesn't overwrite the saved page.
  useEffect(() => {
    if (!pageRestored.current) return;
    if (forecastPagination.currentPage) {
      FilterStore.FAsavePage(forecastPagination.currentPage);
    }
  }, [forecastPagination.currentPage]);

  // Save forecastPagination perPage
  useEffect(() => {
    if (forecastPagination.itemsPerPage) {
      FilterStore.FAsavePerPage(forecastPagination.itemsPerPage);
    }
  }, [forecastPagination.itemsPerPage]);

  // Save approvedFixedAssetsPagination currentPage. Same gate pattern.
  useEffect(() => {
    if (!pageRestored.current) return;
    if (approvedFixedAssetsPagination.currentPage) {
      FilterStore.FAasavePage(approvedFixedAssetsPagination.currentPage);
    }
  }, [approvedFixedAssetsPagination.currentPage]);

  // Save approvedFixedAssetsPagination perPage
  useEffect(() => {
    if (approvedFixedAssetsPagination.itemsPerPage) {
      FilterStore.FAasavePerPage(approvedFixedAssetsPagination.itemsPerPage);
    }
  }, [approvedFixedAssetsPagination.itemsPerPage]);

  // Save masterlistPagination currentPage. Same gate pattern.
  useEffect(() => {
    if (!pageRestored.current) return;
    if (masterlistPagination.currentPage) {
      FilterStore.FAmsavePage(masterlistPagination.currentPage);
    }
  }, [masterlistPagination.currentPage]);

  // Save masterlistPagination perPage
  useEffect(() => {
    if (masterlistPagination.itemsPerPage) {
      FilterStore.FAmsavePerPage(masterlistPagination.itemsPerPage);
    }
  }, [masterlistPagination.itemsPerPage]);

  // Restore all pages on mount. Deferred with setTimeout so it runs after
  // fetchCutOff → reloadTable → updateParams → reset-to-1 has settled,
  // then opens the save gate so user navigation is persisted from here on.
  useEffect(() => {
    const savedFA = FilterStore.FAgetPage();
    const savedFAa = FilterStore.FAagetPage();
    const savedFAm = FilterStore.FAmgetPage();
    if (savedFA > 1 || savedFAa > 1 || savedFAm > 1) {
      setTimeout(() => {
        forecastPagination.setCurrentPage(savedFA);
        approvedFixedAssetsPagination.setCurrentPage(savedFAa);
        masterlistPagination.setCurrentPage(savedFAm);
        pageRestored.current = true;
      }, 0);
    } else {
      setTimeout(() => {
        pageRestored.current = true;
      }, 0);
    }
  }, []);

  // Reset to page 1 on search/column filter change. Skips first run so it
  // doesn't stomp the page just restored above. Date range changes are
  // handled directly in handleDateRangeChange.
  useEffect(() => {
    if (isFirstFilterReset.current) {
      isFirstFilterReset.current = false;
      return;
    }
    forecastPagination.setCurrentPage(1);
    FilterStore.FAsavePage(1);
    approvedFixedAssetsPagination.setCurrentPage(1);
    FilterStore.FAasavePage(1);
    masterlistPagination.setCurrentPage(1);
    FilterStore.FAmsavePage(1);
  }, [searchText, filterColumn]);
  //END

  useEffect(() => {
    fetchCutOff();
    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="h-100 w-100 border bg-white custom-container">
      {isLoading ? (
        <div className="loading-container">
          <ThreeDot
            variant="brick-stack"
            color="#6290FE"
            size="large"
            text="Loading Data..."
            textColor=""
          />
        </div>
      ) : authrztn.includes("FixedAssets-View") ? (
        <>
          <div className="w-100 p-2 d-flex flex-row justify-content-between">
            <div className="d-flex flex-column title-custom">
              <span className="fs-3">FIXED ASSETS</span>
            </div>
            <div className="d-flex align-items-center">
              <span
                onClick={() => handleOpenFixedAsset()}
                className="mx-3 text-primary"
                title="Pending"
                style={{
                  textDecoration: "underline",
                  display: "inline-block",
                  transition: "transform 0.2s ease-in-out",
                  cursor: "pointer",
                  opacity: 1, // Key change: use opacity
                }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.transform = "scale(1.1)")
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.transform = "scale(1)")
                }
              >
                Pending
              </span>
              <div>
                {authrztn.includes("FixedAssets-Add") && (
                  <Link
                    to="/accounting/create-fixed-assets"
                    className="btn btn-primary d-flex flex-row align-items-center title-button"
                  >
                    <i className="bx bx-plus fs-5"></i> Create
                  </Link>
                )}
              </div>
            </div>
          </div>

          {/* Widgets */}
          <div className="row mt-3">
            <div className="col-md-3">
              <div className="card text-center">
                <div className="card-body">
                  <h5 className="card-title">
                    <span>
                      <i className="bx bx-bar-chart-alt fs-5 h-100" />
                    </span>
                    Total Cost
                  </h5>
                  <p
                    className="card-text amount"
                    title={formatToTwoDecimal(widgets.totalCost)}
                    // Same green as the Customer widgets (lighter in dark mode)
                    style={{
                      cursor: "default",
                      color: "var(--widget-number-color)",
                    }}
                  >
                    {formatToPHP(widgets.totalCost)}
                  </p>
                </div>
              </div>
            </div>
            <div className="col-md-3">
              <div className="card text-center">
                <div className="card-body">
                  <h5 className="card-title">
                    <span>
                      <i className="bx bx-trending-down me-1 h-100" />
                    </span>
                    Depreciated Amount
                  </h5>
                  <p
                    className="card-text amount"
                    title={formatToTwoDecimal(widgets.depreciatedAmount)}
                    // Same green as the Customer widgets (lighter in dark mode)
                    style={{
                      cursor: "default",
                      color: "var(--widget-number-color)",
                    }}
                  >
                    {formatToPHP(widgets.depreciatedAmount)}
                  </p>
                </div>
              </div>
            </div>
            <div className="col-md-3">
              <div className="card text-center">
                <div className="card-body">
                  <h5 className="card-title d-flex justify-content-center align-items-center">
                    <span>
                      <i className="bx bx-credit-card fs-5 me-1 pt-1" />
                    </span>
                    <span>Net Value</span>
                  </h5>
                  <p
                    className="card-text amount"
                    title={formatToTwoDecimal(widgets.netValue)}
                    // Same green as the Customer widgets (lighter in dark mode)
                    style={{
                      cursor: "default",
                      color: "var(--widget-number-color)",
                    }}
                  >
                    {formatToPHP(widgets.netValue)}
                  </p>
                </div>
              </div>
            </div>
            <div className="col-md-3">
              <div className="card text-center">
                <div className="card-body">
                  <h5 className="card-title d-flex justify-content-center align-items-center">
                    <span>
                      <i className="bx bxs-credit-card fs-5 me-1 pt-1" />
                    </span>
                    <span>Current Depreciation</span>
                  </h5>
                  <p
                    className="card-text amount"
                    title={formatToTwoDecimal(widgets.currentDepreciation)}
                    // Same green as the Customer widgets (lighter in dark mode)
                    style={{
                      cursor: "default",
                      color: "var(--widget-number-color)",
                    }}
                  >
                    {formatToPHP(widgets.currentDepreciation)}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Date filter & Clear All Filters */}
          <div className="container-fluid mt-4 p-0">
            <div className="row mx-auto align-items-end">
              <div className="col-sm mb-2">
                <DateRangePicker
                  startDate={
                    cutOffDate?.from ? new Date(cutOffDate.from) : null
                  }
                  endDate={cutOffDate?.to ? new Date(cutOffDate.to) : null}
                  onDateRangeChange={handleDateRangeChange}
                  label="Date Range"
                />
              </div>
              <div className="col-sm d-flex flex-row align-items-end mb-2 filter-btn-container w-100">
                <button
                  className="btn btn-primary w-100"
                  onClick={handleClearFilters}
                >
                  <i className="bx bx-reset me-1"></i> Clear All Filters
                </button>
              </div>
            </div>
          </div>

          {/* Search filter */}
          <div className="w-100 mt-4 container-fluid">
            <div className="input-group">
              <input
                type="text"
                className="form-control"
                placeholder="Search..."
                value={searchText}
                onChange={(e) => handleSearchChange(e.target.value)}
              />
              <button
                type="button"
                className="btn btn-outline-secondary dropdown-toggle dropdown-toggle-split"
                data-bs-toggle="dropdown"
                aria-expanded="false"
              >
                <i className="fa-solid fa-sliders"></i>
                <span className="visually-hidden">Toggle Dropdown</span>
              </button>
              <ul className="dropdown-menu dropdown-menu-end">
                <li>
                  <button
                    className={`dropdown-item ${
                      filterColumn === "all" ? "active" : ""
                    }`}
                    onClick={() => handleFilterColumn("all")}
                  >
                    All
                  </button>
                </li>
                <li>
                  <button
                    className={`dropdown-item ${
                      filterColumn === "transaction_code" ? "active" : ""
                    }`}
                    onClick={() => handleFilterColumn("transaction_code")}
                  >
                    Item Code
                  </button>
                </li>
                <li>
                  <button
                    className={`dropdown-item ${
                      filterColumn === "product_name" ? "active" : ""
                    }`}
                    onClick={() => handleFilterColumn("product_name")}
                  >
                    Product Name
                  </button>
                </li>
              </ul>
            </div>
          </div>

          <div className="w-100 mt-3 container-fluid">
            <Tabs
              transition={false}
              mountOnEnter
              unmountOnExit={false}
              activeKey={activeTab}
              onSelect={(tab) => {
                setActiveTab(tab);
                FilterStore.saveTab(tab);
              }}
              id="fixed-asset-tabs"
              className="mb-3"
            >
              <Tab eventKey={"masterlist"} title={"Masterlist"}>
                <div className="w-100 mt-3 container-fluid">
                  <div className="data-table-cell-width">
                    <DataTable
                      columns={masterlistSort.sortColumns(masterlistColumns)}
                      data={masterlistPagination.data}
                      customStyles={customStyles}
                      onRowClicked={(row) => handleRowClicked(row.id)}
                      className="dataTable"
                      {...masterlistSort.sortProps}
                    />
                    <PaginationControls {...masterlistPagination} />
                  </div>
                </div>
              </Tab>

              <Tab eventKey="depreciation" title={"Depreciation"}>
                <div className="w-100 mt-3 container-fluid">
                  <div className="data-table-cell-width">
                    <DataTable
                      columns={forecastSort.sortColumns(forecastColumns)}
                      data={forecastPagination.data}
                      customStyles={customStyles}
                      onRowClicked={(row) =>
                        handleRowClicked(row.fixed_asset_id)
                      }
                      className="dataTable"
                      {...forecastSort.sortProps}
                    />
                    <PaginationControls {...forecastPagination} />
                  </div>
                </div>
              </Tab>
              <Tab eventKey={"expenses"} title={"Expenses Payable"}>
                <div className="w-100 mt-3 container-fluid">
                  <div className="data-table-cell-width">
                    <DataTable
                      columns={approvedSort.sortColumns(expensesColumns)}
                      data={approvedFixedAssetsPagination.data}
                      customStyles={customStyles}
                      onRowClicked={(row) => handleRowClicked(row.id)}
                      className="dataTable"
                      {...approvedSort.sortProps}
                    />
                    <PaginationControls {...approvedFixedAssetsPagination} />
                  </div>
                </div>
              </Tab>
            </Tabs>
          </div>
        </>
      ) : (
        <div className="no-access">
          <img src={NoAccess} alt="NoAccess" className="no-access-img" />
          <h3>You don't have access to this function.</h3>
        </div>
      )}

      {/* Fixed Asset Modal */}
      <Modal
        show={showFixedAsset}
        onHide={handleCloseFixedAsset}
        size="lg"
        backdrop="static"
        dialogClassName="custom-modal-vw90"
      >
        <Modal.Header closeButton>
          <Modal.Title className="w-100">
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <span className="fw-semibold fs-5">Fixed Asset</span>
              </div>
            </div>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {pendingFixedAssetsPagination?.data?.length ? (
            <>
              <div className="data-table-cell-width">
                <DataTable
                  columns={pendingSort.sortColumns(fixedAssetColumns)}
                  data={pendingFixedAssetsPagination.data}
                  customStyles={customStyles}
                  fixedHeader
                  fixedHeaderScrollHeight="calc(100vh - 13rem)"
                  onRowClicked={(row) => handleRowClicked(row.id)}
                  className="dataTable"
                  {...pendingSort.sortProps}
                />
                <PaginationControls {...pendingFixedAssetsPagination} />
              </div>
            </>
          ) : (
            <div className="alert alert-info">
              <i className="fa-solid fa-info-circle me-2"></i>
              No fixed assets exist for the selected dates. Try adjusting the
              date range.
            </div>
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default NewFixedAsset;
